import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { WidgetPreview } from '@/components/widget-builder/WidgetPreview'
import { toast } from '@/components/ui/use-toast'
import {
	widgetFormSchema,
	type WidgetFormValues,
	defaultWidgetValues,
} from '@/lib/widget-schema'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Skeleton } from '@/components/ui/skeleton'
import { Breadcrumb } from '@/components/Breadcrumb'
import { Edit, Loader2, ArrowLeft, ArrowRight } from 'lucide-react'
import { motion } from 'framer-motion'
import { apiClient, ApiWidget } from '@/lib/api'
import Stepper from '@/components/widget-builder/Stepper'
import { Step1_Functionality } from '@/components/widget-builder/steps/Step1_Functionality'
import { Step2_Content } from '@/components/widget-builder/steps/Step2_Content'
import { Step3_Appearance } from '@/components/widget-builder/steps/Step3_Appearance'
import { Step4_Behavior } from '@/components/widget-builder/steps/Step4_Behavior'
import { useProgram } from '@/hooks/useProgram'
import { useTeamSubscription } from '@/hooks/useTeamSubscription'
import { useTeamContext } from '@/context/TeamContext'
import { useTranslation } from '@/hooks/useTranslation'

// Helper function to convert rgba to hex (if needed)
const ensureHexColor = (color: string | null | undefined, defaultColor: string): string => {
	if (!color) return defaultColor
	
	// If already hex, return as is
	if (color.startsWith('#')) return color
	
	// If rgba, return default for now (could implement conversion later)
	if (color.startsWith('rgba') || color.startsWith('rgb')) {
		return defaultColor
	}
	
	return color
}

// Helper function to transform API data to Form data
const transformApiToForm = (apiData: ApiWidget): WidgetFormValues => {
	// Handle legacy 'feedback' type values
	
	// Extract theme configuration from JSONB field
	const themeConfig = apiData.theme_config || {}

	// Transform type field - handle legacy 'feedback' value
	const transformedType = apiData.type && ['nps', 'csat', 'custom', 'testimonial'].includes(apiData.type) 
		? (apiData.type as 'nps' | 'csat' | 'custom' | 'testimonial')
		: apiData.primary_feedback_type && ['nps', 'csat', 'custom', 'testimonial'].includes(apiData.primary_feedback_type)
		? (apiData.primary_feedback_type as 'nps' | 'csat' | 'custom' | 'testimonial')
		: undefined;


	// Ensure targeting data is properly structured
	const targeting = apiData.targeting || {}
	const transformedTargeting = {
		urlPatterns: Array.isArray(targeting.urlPatterns)
			? targeting.urlPatterns
			: [],
		deviceTypes: Array.isArray(targeting.deviceTypes)
			? targeting.deviceTypes
			: ['desktop', 'mobile', 'tablet'],
		userProperties:
			typeof targeting.userProperties === 'object'
				? targeting.userProperties
				: {},
		excludeUrlPatterns: Array.isArray(targeting.excludeUrlPatterns)
			? targeting.excludeUrlPatterns
			: [],
	}

		const transformedData = {
			name: apiData.name || '',
			enable_survey: apiData.enable_survey ?? true,
			enable_bug_reporting: apiData.enable_bug_reporting ?? false,
			enable_contact_messages: apiData.enable_contact_messages ?? false,
			enable_feature_requests: apiData.enable_feature_requests ?? false,
			enable_reviews: apiData.enable_reviews ?? false,
			enable_live_chat: apiData.enable_live_chat ?? false,
		position:
			(apiData.position as WidgetFormValues['position']) || 'bottom-right',
		primaryColor: ensureHexColor(apiData.primary_color, '#0066FF'),
		gradientEndColor: apiData.gradient_end_color ? ensureHexColor(apiData.gradient_end_color, null) : null,
		backgroundColor: apiData.background_color || '#FFFFFF', // colorSchema accepts both hex and rgba
		textColor: ensureHexColor(apiData.text_color, '#000000'),
		feedbackButton: {
			show: apiData.show_feedback_button ?? true,
			text: apiData.feedback_button_text || 'Feedback',
			color: ensureHexColor(apiData.feedback_button_color, '#0066FF'),
			textColor: ensureHexColor(apiData.feedback_button_text_color, '#FFFFFF'),
		},
		widgetTriggerSize: 'm',
		widgetTriggerIcon: '',
		showBranding: apiData.show_branding ?? true,
		showHeader: true, // Default value from schema
		// Map theme-specific fields from JSONB
		brandingStyle: themeConfig.brandingStyle || {},
		menuItemColors: themeConfig.menuItemColors || {},
		menuItemStyle: themeConfig.menuItemStyle || { layout: 'default' },
		animation: themeConfig.animation || { header: 'none' },
		glassmorphism: themeConfig.glassmorphism || { enabled: false },
		layout: themeConfig.layout || { spacing: 'default', containerBorder: true },
		header_title: apiData.header_title ?? '', // Use ?? to preserve null/undefined as empty string
		primary_feedback_type: (apiData.primary_feedback_type || 'nps') as
			| 'nps'
			| 'csat'
			| 'custom'
			| 'testimonial',
		type: transformedType,
		question: apiData.question || '',
		customQuestions: (apiData.custom_questions || [])
			.map((q) => ({
				id: q.id || crypto.randomUUID(),
				type: q.type as 'text' | 'email' | 'multiple_choice' | 'scale',
				question: q.question || '',
				required: q.required ?? false,
				options: q.options || [],
				order_index: q.order_index ?? 0,
				min_scale: q.min_scale,
				max_scale: q.max_scale,
			}))
			.sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)),
		buttonText: apiData.button_text || 'Submit',
		thankYou: apiData.thank_you || {
			title: 'Thank you!',
			message: 'We appreciate your feedback.',
		},
		targeting: transformedTargeting,
		trigger: {
			type: apiData.trigger?.type || 'immediate',
			delay: apiData.trigger?.delay,
			scrollPercentage: apiData.trigger?.scrollPercentage,
		},
	}

	return transformedData
}

// Helper function to convert userProperties array to object (if needed by step components)
const convertArrayToObject = (
	arr: { key: string; value: string }[] | undefined
): Record<string, string> => {
	const obj: Record<string, string> = {}
	if (Array.isArray(arr)) {
		arr.forEach((item) => {
			if (item.key && item.value) {
				obj[item.key] = item.value
			}
		})
	}
	return obj
}

const EditWidget = () => {
	const { id: widgetId } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [currentStep, setCurrentStep] = useState(1)
	const totalSteps = 4
	const { t } = useTranslation()
	const stepNames = [
		t('editWidget.stepFunctionality'),
		t('editWidget.stepContent'),
		t('editWidget.stepAppearance'),
		t('editWidget.stepBehavior')
	]
	const { programId } = useProgram()
	const { teamId } = useTeamContext()
	const { data: subscriptionData, isLoading: isSubscriptionLoading } =
		useTeamSubscription(teamId)

	// Determine premium status (handle loading state)
	const isPremium = useMemo(() => {
		if (isSubscriptionLoading) return false // Default to false while loading
		return subscriptionData?.isPremium || false
	}, [subscriptionData, isSubscriptionLoading])

	// Fetch widget data using the API client
	const {
		data: widgetData,
		isLoading: isWidgetLoading,
		error: widgetError,
	} = useQuery<ApiWidget, Error>({
		queryKey: ['widget', widgetId],
		queryFn: () => {
			if (!widgetId) throw new Error(t('editWidget.widgetIdRequired'))
			return apiClient<ApiWidget>(`/api/widgets/${widgetId}`)
		},
		enabled: !!widgetId,
	})

	// Form setup - Initialize with stable defaults, reset when data loads
	const form = useForm<WidgetFormValues>({
		resolver: zodResolver(widgetFormSchema),
		defaultValues: defaultWidgetValues,
		mode: 'onChange', // Real-time validation for better UX
	})

	// Update form when widget data is loaded or changes
	useEffect(() => {
		if (widgetData) {
			const transformedData = transformApiToForm(widgetData)
			form.reset(transformedData, {
				keepDefaultValues: false, // Ensure complete reset
			})
		}
	}, [widgetData, form])

	// Update widget mutation
	const updateWidgetMutation = useMutation<ApiWidget, Error, WidgetFormValues>({
		mutationFn: async (values: WidgetFormValues) => {
			if (!widgetId) throw new Error(t('editWidget.widgetIdMissing'))

			// Ensure programId is included if needed by the backend schema (it's part of BaseWidgetSchema)
			const payload = {
				...values,
				program_id: programId || widgetData?.program_id, // Use current context or fallback to loaded widget data
			}
			delete (payload as any).widgetTriggerSize
			delete (payload as any).widgetTriggerIcon

			// Important: Remove fields that shouldn't be sent in the PUT request if any
			// delete payload.activeTab; // Example if activeTab was in WidgetFormValues

			return apiClient<ApiWidget>(`/api/widgets/${widgetId}`, {
				method: 'PUT',
				data: payload, // Send the form values
				needsAuth: true,
			})
		},
		onSuccess: (data) => {
			toast({
				title: t('editWidget.success'),
				description: t('editWidget.widgetUpdatedSuccessfully'),
			})
			queryClient.invalidateQueries({ queryKey: ['widgets', programId] }) // Invalidate list
			queryClient.setQueryData(['widget', widgetId], data) // Update specific widget cache
			form.reset(transformApiToForm(data)) // Reset form with updated data to clear dirty state
			navigate(`/widgets/${widgetId}`); // Navigate back to details
		},
		onError: (error) => {
			console.error('Error updating widget:', error)
			toast({
				title: t('editWidget.error'),
				description:
					error.message || t('editWidget.failedToUpdateWidget'),
				variant: 'destructive',
			})
		},
		onSettled: () => {
			setIsSubmitting(false)
		},
	})

	const onSubmit = (values: WidgetFormValues) => {
		// Check for actual validation errors (not just form state flag)
		const errors = form.formState.errors
		const hasActualErrors = Object.keys(errors).length > 0
		
		if (hasActualErrors) {
			
			// Show the first error message to the user
			const firstError = Object.values(errors)[0] as any
			toast({
				title: t('editWidget.validationError'),
				description: firstError?.message || t('editWidget.checkFormInputs'),
				variant: 'destructive',
			})
			return
		}

		setIsSubmitting(true)

		// Ensure program_id is included and handle userProperties conversion if necessary
		const submissionValues = {
			...values,
			program_id: programId || widgetData?.program_id, // Use current context or fallback
			targeting: {
				...values.targeting,
				// Convert array back to object before submitting if step components used the array format
				userProperties: convertArrayToObject(
					(values.targeting as any).userPropertiesArray
				),
			},
		}
		delete (submissionValues as any).widgetTriggerSize
		delete (submissionValues as any).widgetTriggerIcon
		// Remove the temporary array if it exists before submission
		delete (submissionValues.targeting as any).userPropertiesArray

		updateWidgetMutation.mutate(submissionValues) // Mutate with potentially modified values
	}

	// --- Step Navigation ---
	const nextStep = () =>
		setCurrentStep((prev) => Math.min(prev + 1, totalSteps))
	const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1))

	// --- Loading and Error States ---
	if (isWidgetLoading || isSubscriptionLoading) {
		return (
			<div className="p-8 max-w-7xl mx-auto">
				<Skeleton className="h-6 w-1/4 mb-6" /> {/* Breadcrumb Skeleton */}
				<Skeleton className="h-24 w-full mb-6" /> {/* Header Skeleton */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<Skeleton className="h-[600px] lg:col-span-2 rounded-lg" />{' '}
					{/* Form Skeleton */}
					<Skeleton className="h-[500px] rounded-lg" /> {/* Preview Skeleton */}
				</div>
			</div>
		)
	}

	if (widgetError) {
		return (
			<div className="p-8 text-center text-destructive">
				{t('editWidget.errorLoadingWidget')}: {widgetError.message}
			</div>
		)
	}

	if (!widgetData) {
		return (
			<div className="p-8 text-center text-muted-foreground">
				{t('editWidget.widgetNotFound')}
			</div>
		)
	}

	// --- Watch the entire form state for preview ---
	const watchedConfig = form.watch()
	

	// Create the config object for the preview, including program_id
	const previewConfig = {
		...watchedConfig,
		// Ensure program_id is passed to preview if required by WidgetConfig type
		program_id: programId || widgetData?.program_id,
	}

	return (
		<div className="p-4 md:p-8 max-w-7xl mx-auto">
			<Breadcrumb
				className="mb-6"
				items={[
					{ name: t('editWidget.dashboard'), url: '/' },
					{ name: t('editWidget.widgets'), url: '/widgets' },
					{ name: t('editWidget.editWidget'), url: `/widgets/edit/${widgetId}` },
				]}
			/>
			<div className="space-y-6">
				{/* Header - Keep simplified header, remove buttons */}
				<motion.div
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-6 shadow-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
					<div>
						<div className="flex items-center gap-2">
							{/* Use primary color from theme or default */}
							<div className="p-2 bg-primary/10 rounded-lg">
								<Edit className="h-6 w-6 text-primary" />
							</div>
							<h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
								{t('editWidget.editWidget')}
								{widgetData?.name && `: ${widgetData.name}`}
							</h1>
						</div>
						<p className="text-sm md:text-base text-muted-foreground mt-1">
							{t('editWidget.updateConfigurationDescription')}
						</p>
					</div>
					{/* Buttons removed from header */}
				</motion.div>

				{/* Stepper */}
				<Stepper
					currentStep={currentStep}
					totalSteps={totalSteps}
					stepNames={stepNames}
					onStepClick={(step) => setCurrentStep(step)} // Allow clicking steps
					allowFutureClicks={true} // Add this prop for edit mode
				/>

				{/* Wizard Content */}
				<Form {...form}>
					{/* Use form.handleSubmit here, triggered by the final submit button */}
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
						<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
							{/* Left Column: Steps */}
							<motion.div
								key={currentStep} // Add key to trigger animation on step change
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: 20 }}
								transition={{ duration: 0.3 }}
								className="lg:col-span-3 space-y-6">
								{/* Render current step */}
								{currentStep === 1 && (
									<Step1_Functionality form={form} isPremium={isPremium} />
								)}
								{currentStep === 2 && (
									<Step2_Content form={form} isPremium={isPremium} />
								)}
								{currentStep === 3 && (
									<Step3_Appearance form={form} isPremium={isPremium} />
								)}
								{currentStep === 4 && (
									<Step4_Behavior form={form} isPremium={isPremium} />
								)}

								{/* Navigation Buttons within the form column */}
								<div className="flex justify-between items-center pt-6 border-t">
									<Button
										type="button"
										variant="outline"
										onClick={prevStep}
										disabled={currentStep === 1}>
										<ArrowLeft className="mr-2 h-4 w-4" /> {t('editWidget.previous')}
									</Button>
									{currentStep < totalSteps ? (
										<Button type="button" onClick={nextStep}>
											{t('editWidget.next')} <ArrowRight className="ml-2 h-4 w-4" />
										</Button>
									) : (
										<Button
											type="submit"
											disabled={isSubmitting}>
											{isSubmitting ? (
												<>
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
													{t('editWidget.saving')}
												</>
											) : (
												t('editWidget.saveChanges')
											)}
										</Button>
									)}
								</div>
							</motion.div>

							{/* Right Column: Preview */}
							<div className="lg:col-span-1 space-y-6 sticky top-24 self-start">
								<WidgetPreview
									config={previewConfig as any} // Cast the pre-built object
									showManualTrigger={true}
								/>
							</div>
						</div>
					</form>
				</Form>
			</div>
		</div>
	)
}

export default EditWidget
